import { HttpErrorResponse } from '@angular/common/http';
import { computed, inject, Injectable, InjectionToken, Signal, signal, WritableSignal } from '@angular/core';
import { finalize, Observable, shareReplay, tap } from 'rxjs';
import { BaseStateService } from '../base-state.service';
// Deep imports on purpose: this service is reached from the web app's initial chunk (via
// AuthSessionService), and the `@bpp-stella/api/stella` barrel would drag every api service
// used anywhere in the app into that chunk with it.
import { OnboardingApiService } from '@bpp-stella/api/stella/api/onboarding-api.service';
import { LinkFirebaseAccountDto, ValidateInvitationCodeDto } from '@bpp-stella/api/stella/model/models';

/** Optional side effect after a successful activate (the app tracks StellaAccountErstellt). */
export const ACCOUNT_ACTIVATED_HOOK = new InjectionToken<() => void>('ACCOUNT_ACTIVATED_HOOK');

/**
 * Carries the registration across its screens: the invitation code is checked
 * BEFORE any Firebase account exists (code screen), and redeemed after the
 * account has been created (method / account screen).
 *
 * State is in-memory only. A reload drops the validated code, which is the safe
 * direction — every screen behind the code screen re-checks it and sends the
 * user back if it is gone.
 */
@Injectable({ providedIn: 'root' })
export class RegistrationService implements BaseStateService {
  private readonly onboardingApiService = inject(OnboardingApiService);
  private readonly onActivated = inject(ACCOUNT_ACTIVATED_HOOK, { optional: true });

  private readonly _code: WritableSignal<string | null> = signal(null);
  /** The code accepted by validate-code, held until activate consumes it. */
  public readonly code: Signal<string | null> = computed(() => this._code());

  private readonly _isSubmitting: WritableSignal<boolean> = signal(false);
  public readonly isSubmitting: Signal<boolean> = computed(() => this._isSubmitting());

  private readonly _isFlowActive: WritableSignal<boolean> = signal(false);
  /**
   * True from the moment the code is accepted until the flow ends.
   *
   * Between creating the Firebase account and redeeming the code the user IS an
   * unlinked Firebase user. The consuming app's routing treats an unlinked Firebase
   * user as needing activation; this flag tells it the registration screens
   * already handle that, so it leaves them alone rather than redirecting away.
   */
  public readonly isFlowActive: Signal<boolean> = computed(() => this._isFlowActive());

  /**
   * Non-consuming check of the invitation code (204 valid, 400 invalid/expired,
   * 429 rate-limited). Keeps the code on success so activate can redeem it once
   * the Firebase account exists.
   */
  public validateCode(code: string): Observable<unknown> {
    this._isSubmitting.set(true);

    const dto: ValidateInvitationCodeDto = { einladungsCode: code };

    return this.onboardingApiService.apiOnboardingValidateCodePost(dto).pipe(
      tap(() => {
        this._code.set(code);
        this._isFlowActive.set(true);
      }),
      finalize(() => this._isSubmitting.set(false)),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  /**
   * Redeems the held code for the freshly created Firebase user. The code is
   * one-time: a failure here means it is gone, so the caller has to restart at a
   * code screen rather than retry.
   */
  public activate(firebaseId: string): Observable<unknown> {
    return this.activateWithCode(firebaseId, this._code() ?? '');
  }

  /**
   * Redeems a code that was never validated separately — the linking screen for
   * users whose Firebase account already exists, where activate's own 400 is the
   * only check needed.
   */
  public activateWithCode(firebaseId: string, code: string): Observable<unknown> {
    this._isSubmitting.set(true);

    const dto: LinkFirebaseAccountDto = {
      firebaseId: firebaseId,
      einladungsCode: code
    };

    return this.onboardingApiService.apiOnboardingActivatePost(dto).pipe(
      tap(() => {
        this._code.set(null);
        this._isFlowActive.set(true);
        this.onActivated?.();
      }),
      finalize(() => this._isSubmitting.set(false)),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  public resetState(): void {
    this._code.set(null);
    this._isSubmitting.set(false);
    this._isFlowActive.set(false);
  }

  public handleError(error: HttpErrorResponse): void {
    console.error('An error occurred in RegistrationService:', error);
  }
}
